import { Component, Input, OnChanges, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { ProjectSafetyEventFrequency } from "../safety.service";
import { PermissionConstants } from "src/app/constants/permissions";
import { OAuthService } from "src/app/shared/services/oauth.service";
import { LoggerService } from "src/app/shared/services/logger.service";
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SafetyActions } from '../state/safety.actions';
import { selectEventFrequency, selectDaysWithoutEvent, selectLoading } from '../state/safety.selectors';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
    selector: 'ri-safety-widget',
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatDividerModule,
        MatButtonModule,
        SharedModule
    ],
    templateUrl: './safety-widget.component.html',
    styleUrls: ['./safety-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetyWidgetComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() projectId!: number|null; // Receive project ID as input

  totalEvents: number = 0;
  frequency: number = 0;
  daysWithoutEvent: number = 0;
  isLoading: boolean = true;
  hasError: boolean = false;
  permissions=PermissionConstants;
  hasExtendedPermissions: boolean = false;
  isExpanded = false;

  constructor(
    private store: Store,
    private authService: OAuthService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Subscribe to loading state from store
    this.store.select(selectLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
        this.cdr.markForCheck();
      });

    // Subscribe to event frequency data
    this.store.select(selectEventFrequency)
      .pipe(
        takeUntil(this.destroy$),
        filter(freq => freq !== null)
      )
      .subscribe(data => {
        this.totalEvents = data.totalEvents;
        this.frequency = data.eventFrequencyRate;
        this.cdr.markForCheck();
      });

    // Subscribe to days without event
    this.store.select(selectDaysWithoutEvent)
      .pipe(takeUntil(this.destroy$))
      .subscribe(days => {
        this.daysWithoutEvent = days;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngOnChanges(): void {
    // Evaluate required permissions once and combine (user gets actions if any of these are true)
    Promise.all([
      this.authService.hasPermission(this.permissions.SafetyEventsPermissions.CanAddSafetyEvents),
      this.authService.hasPermission(this.permissions.SafetyEventsPermissions.CanViewSafetyEvents),
      this.authService.hasPermission(this.permissions.SafetyEventsPermissions.CanSeeSafetyEventsReports)
    ]).then(([canAdd, canView, canSeeReports]) => {
      this.logger.debug('SafetyWidgetComponent', 'Safety Widget Permissions:', {
        canAdd,
        canView,
        canSeeReports
      });
      this.hasExtendedPermissions = !!(canAdd || canView || canSeeReports);
      this.cdr.markForCheck();
    });
    if (!this.projectId) {
      this.hasError = true;
      return;
    } else {
      this.hasError = false;
      this.loadData();
    }
  }

  loadData(): void {
    if (!this.projectId) return;
    const projectId = this.projectId;
    // daysWithoutEvent is available to anyone who can see the widget — the home render guard
    // already requires CanViewSafetyEvents or a project-management permission.
    this.store.dispatch(SafetyActions.loadDaysWithoutEvent({ projectId }));
    // The frequency endpoint (which also supplies the incidents/totalEvents count) requires
    // the narrower CanViewEventFrequency permission — NOT part of the widget's render guard.
    // Fetching it unconditionally makes the backend return 403 on every dashboard load for
    // users without it. Both stats it feeds are gated on the same permission in the template,
    // so only fetch when the user actually has it.
    this.authService
      .hasPermission(this.permissions.SafetyEventsPermissions.CanViewEventFrequency)
      .then(canViewFrequency => {
        if (canViewFrequency && this.projectId === projectId) {
          this.store.dispatch(SafetyActions.loadEventFrequency({ projectId }));
        }
      });
  }

  // Determine icon and CSS class for "Days Without Event"
  getDaysIcon(): string {
    if (this.daysWithoutEvent < 7) return 'warning';
    if (this.daysWithoutEvent < 30) return 'info';
    return 'verified';
  }

  getDaysClass(): string {
    if (this.daysWithoutEvent < 7) return 'stat-danger';
    if (this.daysWithoutEvent < 30) return 'stat-warning';
    return 'stat-success';
  }

  /** @deprecated use getDaysClass() */
  getDaysColor(): string { return ''; }

  // Determine icon and CSS class for "Total Events"
  getEventIcon(): string {
    return this.totalEvents === 0 ? 'check_circle' : 'report_problem';
  }

  getEventClass(): string {
    return this.totalEvents === 0 ? 'stat-success' : 'stat-danger';
  }

  /** @deprecated use getEventClass() */
  getEventColor(): string { return ''; }

  // Determine icon and CSS class for "Accident Frequency"
  getFrequencyIcon(): string {
    return this.frequency === 0 ? 'check_circle' : 'trending_up';
  }

  getFrequencyClass(): string {
    return this.frequency === 0 ? 'stat-success' : 'stat-danger';
  }

  /** @deprecated use getFrequencyClass() */
  getFrequencyColor(): string { return ''; }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }
}
